import React, { useState, useEffect, useCallback } from 'react';
import supabase from '../../supabaseClient';
import StripeService from '../../services/stripeService';
import {
  APP_STORE_URL,
  APPLE_MANAGE_SUBSCRIPTIONS_URL,
  getCurrentSubscription,
  hasLegacyStripeSubscription,
  isPremiumSubscription,
  PREMIUM_FEATURES,
  type CurrentSubscription,
} from '../../utils/subscriptionUtils';

const SubscriptionPlans: React.FC = () => {
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [hasLegacyStripe, setHasLegacyStripe] = useState(false);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  const loadSubscriptionState = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [subscription, legacyStripe] = await Promise.all([
        getCurrentSubscription(user.id),
        hasLegacyStripeSubscription(user.id),
      ]);

      setCurrentSubscription(subscription);
      setHasLegacyStripe(legacyStripe);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscriptionState();
  }, [loadSubscriptionState]);

  const isPremium = isPremiumSubscription(currentSubscription);

  const handleManageStripe = async () => {
    try {
      setPortalLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { url } = await StripeService.createBillingPortalSession(
        user.id,
        window.location.href
      );

      if (url) {
        await StripeService.redirectToBillingPortal(url);
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      alert('Could not open Stripe billing portal. Please try again or contact support.');
    } finally {
      setPortalLoading(false);
    }
  };

  const formatRenewalDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="retro-card p-6">
        <div className="text-center py-8 text-[#2d2d2d]">Loading subscription...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      <div className="retro-card p-4 md:p-6">
        <div className="index-tab">
          <h2 className="text-xl font-bold text-[#1f1f1f]">Subscription</h2>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {isPremium ? (
            <div className="rounded-lg border-2 border-[#8aff8a] bg-[#8aff8a]/10 p-5">
              <div className="flex items-start gap-3">
                <span className="text-2xl">✅</span>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#1f1f1f]">
                    {currentSubscription?.plan_name || 'Early Supporter'}
                  </h3>
                  <p className="text-sm text-[#666] mt-1">
                    Active — up to {currentSubscription?.client_limit ?? PREMIUM_FEATURES.CLIENT_LIMIT_PREMIUM} clients
                  </p>
                  {formatRenewalDate(currentSubscription?.current_period_end) && (
                    <p className="text-sm text-[#666] mt-1">
                      Renews {formatRenewalDate(currentSubscription?.current_period_end)}
                    </p>
                  )}
                </div>
              </div>

              {hasLegacyStripe && (
                <button
                  onClick={handleManageStripe}
                  disabled={portalLoading}
                  className="mt-4 neon-button w-full md:w-auto"
                >
                  {portalLoading ? 'Opening portal...' : 'Manage Stripe subscription'}
                </button>
              )}

              {!hasLegacyStripe && (
                <a
                  href={APPLE_MANAGE_SUBSCRIPTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-block neon-button"
                >
                  Manage in App Store
                </a>
              )}
            </div>
          ) : (
            <>
              <div className="rounded-lg border-2 border-[#d4d4d4] bg-white p-5">
                <h3 className="text-lg font-semibold text-[#1f1f1f]">Free plan</h3>
                <p className="text-sm text-[#666] mt-2">
                  Up to {PREMIUM_FEATURES.CLIENT_LIMIT_FREE} active clients. New subscriptions are available on iPhone via the MacTrack app.
                </p>
              </div>

              <div className="rounded-lg border-2 border-[#0eeaff] bg-[#0eeaff]/5 p-5">
                <h3 className="text-lg font-semibold text-[#1f1f1f]">Early Supporter</h3>
                <p className="text-sm text-[#666] mt-2">
                  Unlock up to {PREMIUM_FEATURES.CLIENT_LIMIT_PREMIUM} clients, profile pictures, diet calculator, automated messaging, and advanced meal analysis.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-[#2d2d2d]">
                  <li>£11.99/month or £119.99/year</li>
                  <li>7-day free trial via App Store</li>
                  <li>Subscribe on your iPhone — syncs to this web dashboard automatically</li>
                </ul>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="neon-button success text-center"
                  >
                    Get MacTrack on iPhone
                  </a>
                  <button
                    onClick={loadSubscriptionState}
                    className="neon-button"
                  >
                    Refresh status
                  </button>
                </div>
              </div>

              {hasLegacyStripe && (
                <div className="rounded-lg border border-[#d4d4d4] bg-[#f8f4e6] p-4">
                  <p className="text-sm text-[#666] mb-3">
                    You have an existing Stripe subscription from before the iOS launch.
                  </p>
                  <button
                    onClick={handleManageStripe}
                    disabled={portalLoading}
                    className="neon-button w-full md:w-auto"
                  >
                    {portalLoading ? 'Opening portal...' : 'Manage Stripe subscription'}
                  </button>
                </div>
              )}
            </>
          )}

          <p className="text-xs text-[#888]">
            New web checkout is no longer available. Subscriptions purchased on iPhone unlock premium here once linked to your account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
